/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * "Registration is still open" — the board's one way back to the event.
 *
 * Somebody reading a results board is the warmest possible reader of that
 * event: they are looking at what it was like. If its window is still open,
 * the board should say so once, quietly, and get out of the way.
 *
 * NO NEW BACKEND, AND NO NEW ARITHMETIC. Two decisions already exist in this
 * codebase and both are reused verbatim:
 *
 *   1. WHETHER REGISTRATION IS LIVE — `registrationRules`, the same function
 *      the public event page's own entry button is drawn from. It reads
 *      `event.status === 'REGISTRATION_OPEN'`, the status the platform's
 *      scheduled transitions produced, and it also refuses an invitation-only
 *      event and a registration type this build does not implement.
 *
 *      This board MUST NOT compare `registration_end_datetime` to the clock
 *      itself. The event page does not, so a board that did would eventually
 *      offer "you can still line up" on an event whose own page answers
 *      "registration closed" — the sweep lags, and the two screens would
 *      contradict each other in front of the same person. One source of truth:
 *      the status.
 *
 *   2. WHETHER THIS PERSON IS ALREADY IN — `holdsAnEntry` over the event
 *      page's own `fetchMyRegistrations` count, read by the caller.
 *
 * So this module holds no network code and no dates. It is a pure decision
 * over data somebody else fetched, which is why it is testable in isolation.
 */

import { registrationRules } from '../../../components/athlohive/event-detail/registrationRules';
import { buildRelatedScope } from '../../../utils/relatedScope';

/** The words. Deliberately an aside rather than a second headline. */
export const PROMPT_HEADING = 'Like what you see here?';
export const PROMPT_LINE = 'You can still line up for this one.';
export const PROMPT_CTA = 'Take my spot';

/** Where the button goes — the public event page, the only place entry lives. */
export function eventPathFor(eventId) {
  return `/p/event/${encodeURIComponent(eventId)}`;
}

/**
 * The prompt to draw, or null to draw nothing.
 *
 * @param detail  the `/arcee/public/.../event/:id/` payload ({record, related})
 * @param entered true when this viewer already holds an entry for the event.
 *                A caller that cannot yet tell MUST pass true, not false: the
 *                honest cost of waiting is a prompt that appears a moment
 *                late, and the cost of guessing is inviting somebody who is
 *                already registered to register again.
 */
export function registerPromptFor(detail, { entered = false } = {}) {
  if (entered) return null;

  const record = detail && detail.record;
  const eventId = record && record.id;
  // No id, no destination — and a prompt that cannot be followed is worse
  // than none, so this is the first thing checked rather than the last.
  if (eventId === null || eventId === undefined || eventId === '') return null;

  const rules = registrationRules(record, buildRelatedScope(detail.related));
  if (!rules.canRegister) return null;

  return {
    eventId,
    href: eventPathFor(eventId),
    heading: PROMPT_HEADING,
    line: PROMPT_LINE,
    cta: PROMPT_CTA,
  };
}
